// Colectas → En vivo (ver informes "LYNX — Colectas WebApp: interfaz
// unificada y confirmaciones diarias", "LYNX — Colectas: declaración de
// cantidades y En vivo" y "LYNX — Colectas: cierre funcional"). Consume
// GET /v2/routes/en-vivo para lo operativo (declarado por el chofer) --
// una sola consulta, sin cruzar nada a mano acá.
//
// "Mis colectas" depende de una relación coordinador↔colecta que todavía
// no existe en el backend (ver informe "LYNX — Colectas: diseño ajustado",
// punto 5). Esta pantalla NO inventa esa asignación ni la simula en el
// frontend: muestra la dependencia explícitamente y deja "Todas"
// funcionando de punta a punta, protegida por el mismo db_access que ya
// protege el resto del módulo.
//
// Separación respetada (pedida en el diseño): Colectas = planificación
// (plantillas, horarios, choferes habituales); En vivo = supervisión
// operativa del día + (Etapa 3) conciliación administrativa contra Current.
//
// Etapa 3 -- LYNX "Colectas en vivo: integración WebApp": incorpora acá la
// conciliación individual y masiva ya implementada en el backend
// (conciliacionService.js, Etapas 1 y 2), sin reescribir ninguna regla de
// negocio. La ruta de esta pantalla solo exige db_access (App.js), pero
// los endpoints de conciliación exigen el perfil COORDINADOR o ADMIN_TOTAL
// (requireProfile, mismo criterio que Reportes Operativos) -- por eso toda
// la UI de conciliación queda gateada en el cliente por `canConciliar`
// (adminProfile), igual que ReportesOperativos.tsx: un Picker o un perfil
// Liquidaciones que llegue a esta pantalla sigue viendo exactamente lo
// mismo que antes de esta etapa (solo lo declarado), nunca un botón que
// vaya a devolver 403.
//
// El estado de conciliación de TODAS las paradas del día se trae en una
// sola consulta extra (POST /conciliacion/preview con { fecha }, el mismo
// endpoint que ya existe para la masiva) en vez de una consulta por parada
// -- previewMasivo ya clasifica cada parada PICKUP de la jornada
// (stopsPorJornada es el mismo filtro que usa enVivoSummary), así que no
// hace falta ningún endpoint nuevo para pintar el estado administrativo en
// la lista. El detalle exacto de una parada puntual (monto aprobado,
// saldo, historial) se pide aparte, al expandirla, con el endpoint
// individual ya existente -- nunca antes de que el coordinador lo pida.
//
// El preview nunca garantiza disponibilidad futura: confirmarMasivo()
// vuelve a clasificar desde cero al momento de confirmar. Por eso un
// preview corrido sobre una selección queda invalidado en cuanto la
// selección cambia (massPreviewKey vs. selectionKey), y el resultado de
// una confirmación muestra aprobados y omitidos por separado -- nunca un
// único "éxito" si quedó algo omitido.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type StopSummary = {
  route_id: number; stop_id: number; sequence: number;
  store_id: number | null; store_name: string | null;
  declared_count: number | null; declared_at: string | null; declared_by_driver_id: number | null;
};
type RouteSummary = {
  route_id: number; status: string; scheduled_date: string;
  driver_id: number | null; driver_username: string | null; driver_name: string | null;
  template_id: number | null; template_name: string | null; notes: string | null; departure_time: string | null;
  driver_confirmation: "ACCEPTED" | "DECLINED" | null;
  stops: StopSummary[]; stops_total: number; stops_pending: number;
  total_declared: number | null; last_declared_at: string | null;
  declaration_status: "SIN_INFORMAR" | "PARCIAL" | "INFORMADO";
};

// Contratos reales de conciliacionService.js (ver server/current/routes/
// conciliacionService.js) -- no se inventa ningún campo que el backend no
// devuelva.
type ReconciliationStatus = "PENDIENTE" | "APROBADA" | "DIFERENCIA" | "ANULADA";
type ReconciliationHistoryEntry = {
  decision: ReconciliationStatus;
  declared_count_observado: number | null;
  current_total_observado: number | null;
  saldo_previo_observado: number | null;
  current_disponible_observado: number | null;
  cantidad_aprobada: number | null;
  observacion: string | null;
  actor: string;
  created_at: string;
};
type ReconciliationDetail = {
  route_stop_id: number; store_id: number; fecha: string;
  declared_count: number | null;
  reconciliation_status: ReconciliationStatus;
  reconciliation_amount: number | null;
  reconciled_at: string | null; reconciled_by: string | null;
  current_total: number; saldo_previo: number; current_disponible: number;
  history: ReconciliationHistoryEntry[];
};
type PreviewGrupo = {
  store_id: number; fecha: string; route_stop_ids: number[];
  declarado_total: number; current_total: number; saldo_previo: number; disponible: number; ambiguo: boolean;
};
type AmbiguoExcluido = { route_stop_id: number; store_id: number; fecha: string; motivo: string };
type PreviewResult = {
  route_stop_ids: number[];
  elegibles: number[]; yaAprobadas: number[]; diferenciasPendientes: number[]; sinDeclaracion: number[];
  depositosExcluidos: number[]; ambiguosExcluidos: AmbiguoExcluido[]; grupos: PreviewGrupo[];
};
type ConfirmResult = {
  aprobados: { route_stop_id: number; reconciliation_status: string; reconciliation_amount: number }[];
  omitidos: { route_stop_id: number; motivo: string }[];
};

type Bucket = "APROBADA" | "DIFERENCIA" | "AMBIGUO" | "ELEGIBLE" | "SIN_DECLARAR" | "DEPOSITO";

const isoToday = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : "—");
const hhmmFull = (t: string | null | undefined) => (t ? new Date(t).toLocaleString("es-AR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—");
// Habitual: nombre de la plantilla. Eventual (sin template_id): el nombre
// que se le puso al crearla (notes), o un rótulo por id si quedó vacío.
const colectaName = (r: Pick<RouteSummary, "template_name" | "notes" | "route_id">) => r.template_name ?? (r.notes || `Eventual #${r.route_id}`);

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const smallBadge = (color: string): React.CSSProperties => ({ ...badge(color), padding: "2px 8px", fontSize: "11px" });
const chip = (on: boolean): React.CSSProperties => ({ padding: "6px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? "var(--primary, #4f8cff)" : "var(--border)"}`, color: on ? "var(--primary, #4f8cff)" : "var(--text-secondary)", background: "transparent" });
const textareaStyle: React.CSSProperties = {
  width: "100%", minHeight: "70px", padding: "10px 12px", borderRadius: "12px", border: "1px solid var(--border)",
  outline: "none", background: "var(--surface-alt)", color: "var(--text-primary)", fontSize: "14px", fontFamily: "inherit", resize: "vertical",
};

const CONFIRMATION: Record<string, { label: string; color: string }> = {
  ACCEPTED: { label: "Confirmada", color: "var(--success)" },
  DECLINED: { label: "Rechazada", color: "var(--danger)" },
};
const DECLARATION: Record<RouteSummary["declaration_status"], { label: string; color: string }> = {
  SIN_INFORMAR: { label: "Sin informar", color: "var(--text-secondary)" },
  PARCIAL: { label: "Parcialmente informado", color: "var(--warning, #d29922)" },
  INFORMADO: { label: "Informado", color: "var(--success)" },
};
const RECONCILIATION_STATUS: Record<ReconciliationStatus, { label: string; color: string }> = {
  PENDIENTE: { label: "Pendiente", color: "var(--text-secondary)" },
  APROBADA: { label: "Aprobada", color: "var(--success)" },
  DIFERENCIA: { label: "Diferencia", color: "var(--warning, #d29922)" },
  ANULADA: { label: "Anulada", color: "var(--danger)" },
};
// Resumen por lote (preview masivo): no distingue PENDIENTE de ANULADA (el
// backend tampoco lo necesita para clasificar), eso solo se ve en el
// detalle individual de la parada.
const BUCKET_META: Record<Bucket, { label: string; color: string }> = {
  APROBADA: { label: "Aprobada", color: "var(--success)" },
  DIFERENCIA: { label: "Diferencia", color: "var(--warning, #d29922)" },
  AMBIGUO: { label: "Pendiente · grupo ambiguo", color: "var(--danger)" },
  ELEGIBLE: { label: "Pendiente", color: "var(--text-secondary)" },
  SIN_DECLARAR: { label: "Sin declarar", color: "var(--text-secondary)" },
  DEPOSITO: { label: "Depósito", color: "var(--text-secondary)" },
};
const BUCKET_ORDER: Bucket[] = ["APROBADA", "DIFERENCIA", "AMBIGUO", "ELEGIBLE", "SIN_DECLARAR"];

function buildBucketMap(preview: PreviewResult | null): Map<number, { bucket: Bucket; motivo?: string }> {
  const m = new Map<number, { bucket: Bucket; motivo?: string }>();
  if (!preview) return m;
  for (const id of preview.yaAprobadas) m.set(id, { bucket: "APROBADA" });
  for (const id of preview.diferenciasPendientes) m.set(id, { bucket: "DIFERENCIA" });
  for (const id of preview.sinDeclaracion) m.set(id, { bucket: "SIN_DECLARAR" });
  for (const id of preview.depositosExcluidos) m.set(id, { bucket: "DEPOSITO" });
  for (const a of preview.ambiguosExcluidos) m.set(a.route_stop_id, { bucket: "AMBIGUO", motivo: a.motivo });
  for (const id of preview.elegibles) m.set(id, { bucket: "ELEGIBLE" });
  return m;
}

function Modal({ title, onClose, children, width = "600px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// Panel de conciliación individual de una parada -- colapsado por defecto,
// solo pide el detalle real (GET .../conciliacion) cuando el coordinador lo
// abre. Reutiliza aprobar/diferencia/anular tal cual el backend los expone;
// ninguna regla de elegibilidad se reimplementa acá.
function StopConciliacionPanel({
  routeId, stopId, storeLabel, onChanged,
  req,
}: {
  routeId: number; stopId: number; storeLabel: string; onChanged: () => void;
  req: (path: string, init?: RequestInit) => Promise<any>;
}) {
  const [detail, setDetail] = useState<ReconciliationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [observacion, setObservacion] = useState("");
  const [motivo, setMotivo] = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true); setLocalError(null);
    try {
      const r = await req(`/v2/routes/${routeId}/stops/${stopId}/conciliacion`);
      setDetail(r.reconciliation);
    } catch (e: any) { setLocalError(e.message); }
    finally { setLoading(false); }
  }, [req, routeId, stopId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const runAction = async (action: "aprobar" | "diferencia" | "anular", body?: Record<string, unknown>) => {
    setBusy(true); setLocalError(null);
    try {
      await req(`/v2/routes/${routeId}/stops/${stopId}/conciliacion/${action}`, { method: "POST", body: JSON.stringify(body ?? {}) });
      setObservacion(""); setMotivo("");
      await fetchDetail();
      onChanged();
    } catch (e: any) {
      // 409 = algo cambió entre que se abrió el panel y se confirmó la
      // acción (otra aprobación concurrente, otra anulación). Se informa
      // el motivo real y se vuelve a traer el estado vigente -- nunca se
      // deja la UI mostrando un estado que ya no es cierto.
      setLocalError(e.status === 409 ? `Conflicto: ${e.message}` : e.message);
      await fetchDetail();
    } finally { setBusy(false); }
  };

  if (loading && !detail) return <div style={{ ...panelStyle, marginTop: 8 }}><span style={smallMutedStyle}>Cargando conciliación de {storeLabel}…</span></div>;
  if (!detail) return (
    <div style={{ ...panelStyle, marginTop: 8, borderColor: "var(--danger)" }}>
      <span style={{ color: "var(--danger)" }}>{localError ?? "No se pudo cargar"}</span>{" "}
      <Button variant="secondary" onClick={fetchDetail}>Reintentar</Button>
    </div>
  );

  const st = RECONCILIATION_STATUS[detail.reconciliation_status];
  const puedeAprobarODiferencia = detail.reconciliation_status === "PENDIENTE" || detail.reconciliation_status === "ANULADA";
  const puedeAnular = detail.reconciliation_status === "APROBADA" || detail.reconciliation_status === "DIFERENCIA";

  return (
    <div style={{ ...panelStyle, marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div>
          <span style={badge(st.color)}>{st.label}</span>{" "}
          <span style={smallMutedStyle}>
            Declarado: <b>{detail.declared_count ?? "—"}</b> · Aprobado: <b>{detail.reconciliation_amount ?? "—"}</b> · Disponible en Current: <b>{detail.current_disponible}</b>
            {" "}(total {detail.current_total} − ya aprobado {detail.saldo_previo})
          </span>
        </div>
        <Button variant="secondary" onClick={fetchDetail}>Actualizar</Button>
      </div>

      {localError ? <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 10 }}>{localError}</div> : null}

      {puedeAprobarODiferencia ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              disabled={busy || !(Number.isInteger(detail.declared_count) && (detail.declared_count as number) > 0)}
              onClick={() => { if (window.confirm(`¿Aprobar ${detail.declared_count} unidad/es de ${storeLabel}? Consume saldo disponible de Current compartido con otras colectas de esta tienda y fecha.`)) runAction("aprobar"); }}
            >
              Aprobar {detail.declared_count ?? ""}
            </Button>
            {!(Number.isInteger(detail.declared_count) && (detail.declared_count as number) > 0) ? (
              <span style={smallMutedStyle}>Sin cantidad declarada positiva: no se puede aprobar todavía.</span>
            ) : null}
          </div>
          <div>
            <textarea style={textareaStyle} placeholder="Observación (obligatoria para marcar diferencia)" value={observacion} onChange={(e) => setObservacion(e.target.value)} disabled={busy} />
            <div style={{ marginTop: 8 }}>
              <Button variant="secondary" disabled={busy || !observacion.trim()} onClick={() => runAction("diferencia", { observacion })}>Marcar diferencia</Button>
            </div>
          </div>
        </div>
      ) : null}

      {puedeAnular ? (
        <div style={{ marginBottom: 10 }}>
          <textarea style={textareaStyle} placeholder="Motivo (obligatorio para anular)" value={motivo} onChange={(e) => setMotivo(e.target.value)} disabled={busy} />
          <div style={{ marginTop: 8 }}>
            <Button
              variant="danger"
              disabled={busy || !motivo.trim()}
              onClick={() => { if (window.confirm(`¿Anular la conciliación vigente de ${storeLabel}? Libera el saldo que había consumido.`)) runAction("anular", { motivo }); }}
            >
              Anular conciliación vigente
            </Button>
          </div>
        </div>
      ) : null}

      <details>
        <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: 13 }}>Historial ({detail.history.length})</summary>
        {detail.history.length === 0 ? <p style={smallMutedStyle}>Sin decisiones todavía.</p> : (
          <table style={{ ...tableStyle, marginTop: 8 }}>
            <thead><tr><th style={thStyle}>Cuándo</th><th style={thStyle}>Decisión</th><th style={thStyle}>Cant. aprobada</th><th style={thStyle}>Observación/Motivo</th><th style={thStyle}>Actor</th></tr></thead>
            <tbody>
              {detail.history.map((h, i) => (
                <tr key={i}>
                  <td style={tdStyle}><span style={smallMutedStyle}>{hhmmFull(h.created_at)}</span></td>
                  <td style={tdStyle}><span style={badge(RECONCILIATION_STATUS[h.decision].color)}>{RECONCILIATION_STATUS[h.decision].label}</span></td>
                  <td style={tdStyle}>{h.cantidad_aprobada ?? "—"}</td>
                  <td style={tdStyle}>{h.observacion ?? <span style={smallMutedStyle}>—</span>}</td>
                  <td style={tdStyle}>{h.actor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </div>
  );
}

export default function ColectasEnVivoPage() {
  const { token, adminProfile } = useAuth();
  const canConciliar = adminProfile === "COORDINADOR" || adminProfile === "ADMIN_TOTAL";

  const req = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) {
      const err = new Error(json?.error || `Error ${res.status}`) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }
    return json;
  }, [token]);

  const [tab, setTab] = useState<"mine" | "all">("all");
  const [date, setDate] = useState(isoToday());
  const [routes, setRoutes] = useState<RouteSummary[]>([]);
  const [detail, setDetail] = useState<RouteSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [openStopId, setOpenStopId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const envivo = await req(`/v2/routes/en-vivo?date=${date}`);
      setRoutes(envivo.routes ?? []);
    } catch (e: any) { setError(e.message); return; }
    if (!canConciliar) { setPreview(null); return; }
    try {
      const r = await req(`/v2/routes/conciliacion/preview`, { method: "POST", body: JSON.stringify({ fecha: date }) });
      setPreview(r.preview ?? null);
    } catch (e: any) { setError(e.message); }
  }, [req, date, canConciliar]);
  useEffect(() => { load(); }, [load]);

  const totalColectas = useMemo(() => routes.length, [routes]);
  const bucketByStop = useMemo(() => buildBucketMap(preview), [preview]);
  const stopInfo = useMemo(() => {
    const m = new Map<number, { storeLabel: string; routeLabel: string }>();
    for (const r of routes) for (const s of r.stops) {
      m.set(s.stop_id, { storeLabel: s.store_name ?? `Tienda ${s.store_id}`, routeLabel: colectaName(r) });
    }
    return m;
  }, [routes]);

  const resumenConciliacion = useCallback((r: RouteSummary) => {
    if (!canConciliar || !preview) return null;
    const counts: Partial<Record<Bucket, number>> = {};
    for (const s of r.stops) {
      const b = bucketByStop.get(s.stop_id)?.bucket;
      if (!b || b === "DEPOSITO") continue;
      counts[b] = (counts[b] ?? 0) + 1;
    }
    const parts = BUCKET_ORDER.filter((k) => counts[k]).map((k) => ({ ...BUCKET_META[k], n: counts[k] as number }));
    if (!parts.length) return <span style={smallMutedStyle}>—</span>;
    return (
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {parts.map((p) => <span key={p.label} style={smallBadge(p.color)}>{p.n} {p.label}</span>)}
      </div>
    );
  }, [canConciliar, preview, bucketByStop]);

  // --- Selección y conciliación masiva (Etapa 2, reutilizada tal cual) ---
  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<number>>(new Set());
  const [massPreview, setMassPreview] = useState<PreviewResult | null>(null);
  const [massPreviewKey, setMassPreviewKey] = useState<string | null>(null);
  const [massBusy, setMassBusy] = useState(false);
  const [massResult, setMassResult] = useState<ConfirmResult | null>(null);

  useEffect(() => { setSelectedRouteIds(new Set()); setMassPreview(null); setMassPreviewKey(null); setMassResult(null); }, [date]);

  const selectedStopIds = useMemo(() => {
    const ids: number[] = [];
    for (const r of routes) if (selectedRouteIds.has(r.route_id)) for (const s of r.stops) ids.push(s.stop_id);
    return ids;
  }, [routes, selectedRouteIds]);
  const selectionKey = useMemo(() => [...selectedStopIds].sort((a, b) => a - b).join(","), [selectedStopIds]);

  const toggleRoute = (routeId: number) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId); else next.add(routeId);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedRouteIds((prev) => (prev.size === routes.length ? new Set() : new Set(routes.map((r) => r.route_id))));
  };

  const runMassPreview = async () => {
    setMassBusy(true); setError(null); setMassResult(null);
    try {
      const r = await req(`/v2/routes/conciliacion/preview`, { method: "POST", body: JSON.stringify({ route_stop_ids: selectedStopIds }) });
      setMassPreview(r.preview); setMassPreviewKey(selectionKey);
    } catch (e: any) { setError(e.message); }
    finally { setMassBusy(false); }
  };
  const runMassConfirm = async () => {
    if (massPreviewKey !== selectionKey || !selectedStopIds.length) return;
    if (!window.confirm(`¿Confirmar la conciliación de ${selectedStopIds.length} parada/s? El backend puede rechazar algunas si el saldo cambió desde la vista previa.`)) return;
    setMassBusy(true); setError(null);
    try {
      const r = await req(`/v2/routes/conciliacion/confirmar`, { method: "POST", body: JSON.stringify({ route_stop_ids: selectedStopIds }) });
      setMassResult(r.resultado);
      setMassPreview(null); setMassPreviewKey(null); setSelectedRouteIds(new Set());
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setMassBusy(false); }
  };

  const colCount = 7 + (canConciliar ? 2 : 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <h2 style={titleStyle}>Colectas · En vivo</h2>
        <p style={subtitleStyle}>
          Supervisión operativa del día: lo que cada chofer declaró haber retirado por tienda
          {canConciliar ? ", y la conciliación administrativa de esa cantidad contra lo que efectivamente ingresó a Current." : "."}
        </p>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error} <Button variant="secondary" onClick={() => setError(null)}>Cerrar</Button></div> : null}

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "end", marginBottom: "12px" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={chip(tab === "mine")} onClick={() => setTab("mine")}>Mis colectas</button>
            <button type="button" style={chip(tab === "all")} onClick={() => setTab("all")}>Todas <span style={smallMutedStyle}>· {totalColectas}</span></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "end" }}>
            <Input label="Fecha operativa" type="date" value={date} onChange={(e) => setDate(e.target.value || isoToday())} />
            <Button variant="secondary" onClick={load}>Actualizar</Button>
          </div>
        </div>

        {tab === "mine" ? (
          <div style={panelStyle}>
            <p style={{ margin: 0, color: "var(--text-primary)" }}>«Mis colectas» todavía no está disponible.</p>
            <p style={{ ...smallMutedStyle, margin: "8px 0 0" }}>
              Depende de una relación coordinador↔colecta que no existe en el backend hoy (ver informe «LYNX — Colectas: diseño
              ajustado», Fase 3). No se simula esa asignación desde el frontend: hasta que exista ese vínculo, usá «Todas».
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {canConciliar ? <th style={thStyle}><input type="checkbox" checked={routes.length > 0 && selectedRouteIds.size === routes.length} onChange={toggleAllVisible} /></th> : null}
                  <th style={thStyle}>Colecta</th><th style={thStyle}>Chofer</th><th style={thStyle}>Tiendas</th>
                  <th style={thStyle}>Cantidad informada</th><th style={thStyle}>Estado de declaración</th>
                  {canConciliar ? <th style={thStyle}>Conciliación</th> : null}
                  <th style={thStyle}>Confirmación</th><th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {routes.length === 0 ? <tr><td style={tdStyle} colSpan={colCount}><span style={smallMutedStyle}>Nada generado para {date}.</span></td></tr> : null}
                {routes.map((r) => {
                  const conf = r.driver_confirmation ? CONFIRMATION[r.driver_confirmation] : null;
                  const decl = DECLARATION[r.declaration_status];
                  return (
                    <tr key={r.route_id}>
                      {canConciliar ? <td style={tdStyle}><input type="checkbox" checked={selectedRouteIds.has(r.route_id)} onChange={() => toggleRoute(r.route_id)} /></td> : null}
                      <td style={tdStyle}><b>{colectaName(r)}</b> <span style={smallMutedStyle}>· {hhmm(r.departure_time)} · {r.status}</span></td>
                      <td style={tdStyle}>{r.driver_name ?? r.driver_username ?? <span style={smallMutedStyle}>sin asignar</span>}</td>
                      <td style={tdStyle}>{r.stops_total}{r.stops_pending ? <span style={smallMutedStyle}> · {r.stops_pending} sin informar</span> : null}</td>
                      <td style={tdStyle}>{r.total_declared === null ? <span style={smallMutedStyle}>—</span> : <b>{r.total_declared}</b>}</td>
                      <td style={tdStyle}><span style={badge(decl.color)}>{decl.label}</span></td>
                      {canConciliar ? <td style={tdStyle}>{resumenConciliacion(r)}</td> : null}
                      <td style={tdStyle}>{conf ? <span style={badge(conf.color)}>{conf.label}</span> : <span style={smallMutedStyle}>—</span>}</td>
                      <td style={tdStyle}><Button variant="secondary" onClick={() => setDetail(r)}>Ver detalle</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canConciliar && tab === "all" ? (
        <Card>
          <h3 style={{ margin: "0 0 10px", fontSize: "16px", color: "var(--text-primary)" }}>Conciliación masiva</h3>
          <p style={{ ...smallMutedStyle, margin: "0 0 12px" }}>
            Seleccioná colectas de la tabla de arriba. La vista previa no reserva saldo ni garantiza el resultado: el backend
            vuelve a evaluar todo al confirmar, y puede aprobar solo una parte del lote si algo cambió desde la vista previa.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <span style={smallMutedStyle}>{selectedRouteIds.size} colecta/s seleccionada/s · {selectedStopIds.length} parada/s</span>
            <Button variant="secondary" disabled={!selectedStopIds.length || massBusy} onClick={runMassPreview}>Vista previa</Button>
            <Button disabled={massPreviewKey !== selectionKey || !selectedStopIds.length || massBusy} onClick={runMassConfirm}>Confirmar</Button>
            <Button variant="ghost" disabled={massBusy} onClick={() => { setSelectedRouteIds(new Set()); setMassPreview(null); setMassPreviewKey(null); setMassResult(null); }}>Limpiar selección</Button>
          </div>

          {massPreview ? (
            <div style={{ ...panelStyle, marginBottom: 12 }}>
              <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
                Vista previa{massPreviewKey !== selectionKey ? <span style={{ color: "var(--warning, #d29922)" }}> (desactualizada: la selección cambió, volvé a previsualizar)</span> : null}
              </p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                <span style={smallBadge(BUCKET_META.ELEGIBLE.color)}>{massPreview.elegibles.length} aprobables</span>
                <span style={smallBadge(BUCKET_META.APROBADA.color)}>{massPreview.yaAprobadas.length} ya aprobadas</span>
                <span style={smallBadge(BUCKET_META.DIFERENCIA.color)}>{massPreview.diferenciasPendientes.length} con diferencia pendiente</span>
                <span style={smallBadge(BUCKET_META.SIN_DECLARAR.color)}>{massPreview.sinDeclaracion.length} sin declarar</span>
                <span style={smallBadge(BUCKET_META.DEPOSITO.color)}>{massPreview.depositosExcluidos.length} depósito</span>
                <span style={smallBadge(BUCKET_META.AMBIGUO.color)}>{massPreview.ambiguosExcluidos.length} en grupo ambiguo</span>
              </div>
              {massPreview.ambiguosExcluidos.length ? (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: "pointer", color: "var(--danger)", fontSize: 13 }}>Grupos ambiguos (excluidos, requieren intervención individual)</summary>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {massPreview.ambiguosExcluidos.map((a) => (
                      <li key={a.route_stop_id} style={{ fontSize: 13, marginBottom: 4 }}>
                        <b>{stopInfo.get(a.route_stop_id)?.storeLabel ?? `Tienda ${a.store_id}`}</b> ({stopInfo.get(a.route_stop_id)?.routeLabel ?? `#${a.route_stop_id}`}): {a.motivo}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              {massPreview.grupos.length ? (
                <details>
                  <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: 13 }}>Grupos tienda + fecha ({massPreview.grupos.length})</summary>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {massPreview.grupos.map((g, i) => (
                      <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                        <b>{stopInfo.get(g.route_stop_ids[0])?.storeLabel ?? `Tienda ${g.store_id}`}</b> · {g.fecha} · {g.route_stop_ids.length} colecta/s ·
                        declarado {g.declarado_total} / disponible {g.disponible}{g.ambiguo ? " · AMBIGUO" : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          {massResult ? (
            <div style={{ ...panelStyle, borderColor: massResult.omitidos.length ? "var(--warning, #d29922)" : "var(--success)" }}>
              <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
                Resultado de la confirmación: {massResult.aprobados.length} aprobada/s
                {massResult.omitidos.length ? `, ${massResult.omitidos.length} omitida/s (nunca interpretar esto como éxito total)` : ""}
              </p>
              {massResult.omitidos.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {massResult.omitidos.map((o, i) => (
                    <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                      <b>{stopInfo.get(o.route_stop_id)?.storeLabel ?? `#${o.route_stop_id}`}</b>: {o.motivo}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </Card>
      ) : null}

      {detail ? (
        <Modal title={`${colectaName(detail)} · ${date}`} onClose={() => { setDetail(null); setOpenStopId(null); }} width="760px">
          <p style={{ ...smallMutedStyle, margin: "0 0 12px" }}>
            Chofer: {detail.driver_name ?? detail.driver_username ?? "sin asignar"} · {detail.stops_total} tiendas · total informado:{" "}
            {detail.total_declared === null ? "—" : detail.total_declared}
          </p>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>#</th><th style={thStyle}>Tienda</th><th style={thStyle}>Cantidad</th><th style={thStyle}>Declarado</th>
                {canConciliar ? <th style={thStyle}>Conciliación</th> : null}
                {canConciliar ? <th style={thStyle}></th> : null}
              </tr>
            </thead>
            <tbody>
              {detail.stops.map((s) => {
                const b = bucketByStop.get(s.stop_id);
                const isOpen = openStopId === s.stop_id;
                return (
                  <React.Fragment key={s.stop_id}>
                    <tr>
                      <td style={tdStyle}>{s.sequence}</td>
                      <td style={tdStyle}>{s.store_name ?? `Tienda ${s.store_id}`}</td>
                      <td style={tdStyle}>{s.declared_count === null ? <span style={smallMutedStyle}>Sin informar</span> : <b>{s.declared_count}</b>}</td>
                      <td style={tdStyle}><span style={smallMutedStyle}>{hhmmFull(s.declared_at)}</span></td>
                      {canConciliar ? <td style={tdStyle}>{b ? <span style={badge(BUCKET_META[b.bucket].color)} title={b.motivo}>{BUCKET_META[b.bucket].label}</span> : <span style={smallMutedStyle}>—</span>}</td> : null}
                      {canConciliar ? (
                        <td style={tdStyle}>
                          {s.store_id === null ? null : <Button variant="secondary" onClick={() => setOpenStopId(isOpen ? null : s.stop_id)}>{isOpen ? "Cerrar" : "Conciliar"}</Button>}
                        </td>
                      ) : null}
                    </tr>
                    {canConciliar && isOpen ? (
                      <tr>
                        <td style={{ ...tdStyle, borderTop: "none" }} colSpan={6}>
                          <StopConciliacionPanel
                            routeId={detail.route_id}
                            stopId={s.stop_id}
                            storeLabel={s.store_name ?? `Tienda ${s.store_id}`}
                            req={req}
                            onChanged={load}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Modal>
      ) : null}
    </div>
  );
}
